import {Box, Stack, TableCell, Typography} from "@mui/material";
import React from "react";
import {VirtualTable} from "../../components/data-table/VirtualTable";
import {TableColumnData} from "../../components/data-table/TableColumnData";
import {SiteChip} from "../../components/site-chip/SiteChip";
import EmployeeAvatar from "../../components/employee-avatar/EmployeeAvatar";
import {KeyChip} from "../../components/key-chip/KeyChip";
import EmployeeLink from "../../components/employee-link/EmployeeLink";
import TeamComponent from "../../components/team-component/TeamComponent";
import {idFn} from "../../util/FunctionUtil";
import {HierarchyEmployee} from "../../util/EmployeeHierarchy";
import NeighbourhoodColorIcon from "../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import {today} from "../../util/DateUtil";
import {grey} from "@mui/material/colors";
import {showOverflowOnHoverSx} from "../../components/overflow-on-hover/ShowOverflowOnHover";
import {EmployeeHistoryButton} from "../history-dialog/HistoryDialogContext";
import DeskChip from "../../components/desk-chip/DeskChip";

export function NeighbourhoodCell({row}: { row: HierarchyEmployee }) {
    return (
        <TableCell sx={{verticalAlign: "top"}}>
            {row.neighbourhoods.map(neighbourhood => (
                <Stack gap={1} direction="row" alignItems="center" key={neighbourhood.id} width="168px">
                    <NeighbourhoodColorIcon neighbourhood={neighbourhood} size={16}/>
                    <Box component="span" sx={showOverflowOnHoverSx}>
                        {neighbourhood.name}
                    </Box>
                </Stack>
            ))}
            {row.assignedDesks.length > 0 && (
                <Stack direction="row" gap={0.5} mt={0.5} flexWrap="wrap" width="168px">
                    {row.assignedDesks.map(desk => (
                        <DeskChip
                            key={desk.id}
                            desk={desk}
                            hideAssignee
                            deskAssignee={row}
                            size="small"
                            outsideNeighbourhood={!row.neighbourhoods.some(neighbourhood => neighbourhood.id === desk.neighbourhoodId)}
                        />
                    ))}
                </Stack>
            )}
        </TableCell>
    );
}

function EmployeeKeysCell({row}: { row: HierarchyEmployee }) {
    return (
        <TableCell sx={{verticalAlign: 'top'}}>
            <Stack gap={1} direction="row" flexWrap="wrap" alignItems="center">
                {row.keys.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No keys</Typography>
                ) : (
                    row.keys.map((key) => (
                        <KeyChip key={`${key.key.id}-${key.historyRecord.id}`} keyData={key.key}
                                 record={key.historyRecord}/>
                    ))
                )}
                <EmployeeHistoryButton employee={row}/>
            </Stack>
        </TableCell>
    );
}

const columns: TableColumnData<HierarchyEmployee>[] = [
    {
        key: 'avatar',
        header: '',
        width: '32px',
        cell: (row) => (
            <TableCell sx={{verticalAlign: 'top'}}>
                <EmployeeAvatar employee={row} sx={{width: '42px', height: '42px'}}/>
            </TableCell>
        ),
    },
    {
        key: 'fullName',
        header: 'Name',
        width: '232px',
        cell: (row) => (
            <TableCell sx={{verticalAlign: 'top'}}>
                <Stack direction="column" sx={{width: '200px'}}>
                    <EmployeeLink employee={row} sx={showOverflowOnHoverSx}/>
                    <Typography variant="caption" display="block" color="text.secondary" sx={showOverflowOnHoverSx} title={row.hrEmployeeId ?? ''}>
                        {row.email}
                    </Typography>
                </Stack>
            </TableCell>
        ),
    },
    {
        key: 'residency',
        header: 'Residency',
        width: '124px',
        cell: (row) => (
            <TableCell sx={{verticalAlign: 'top'}}>
                <Box sx={{width: '96px'}}>
                    <SiteChip employee={row}/>
                </Box>
            </TableCell>
        ),
    },
    {
        key: 'team',
        header: 'Job',
        width: '216px',
        cell: (row) => (
            <TableCell sx={{verticalAlign: "top"}}>
                <Box sx={{...showOverflowOnHoverSx, width: '184px'}}>
                    {row.jobTitle && (<Typography variant="body2" color="text.secondary">{row.jobTitle}</Typography>)}
                </Box>
                <Box sx={{...showOverflowOnHoverSx, width: '184px'}}>
                    {row.teamPath && (<TeamComponent teamPath={row.teamPath}/>)}
                </Box>
            </TableCell>
        ),
    },
    {
        key: 'neighbourhood',
        header: 'Neighbourhood',
        width: '200px',
        cell: (row) => (
            <NeighbourhoodCell row={row}/>
        ),
    },
    {
        key: 'startDate',
        header: 'Start',
        width: '96px',
        cell: (row) => (
            <TableCell sx={{verticalAlign: 'top'}}>
                <Box sx={{width: '80px', color: row.startDate && row.startDate > today() ? grey[500] : undefined}}>
                    {row.startDate || ''}
                </Box>
            </TableCell>
        ),
    },
    {
        key: 'keys',
        header: 'Keys',
        cell: (row) => (
            <EmployeeKeysCell row={row}/>
        ),
    },
];

export function EmployeeTable({rows}: {
    rows: HierarchyEmployee[],
}) {
    return (
        <VirtualTable
            columns={columns}
            rows={rows}
            id={idFn}
        />
    );
}
